/**
 * StudentProfile - a student's public page
 * ----------------------------------------
 * Blue banner with their name and rating, then their bio, what they are
 * happy to do, the work they have completed, and every review about them.
 */

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, CheckCircle2, GraduationCap } from 'lucide-react';

import { studentApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { dateOnly, travel } from '../utils/format';
import Avatar from '../components/Avatar';
import Badge from '../components/Badge';
import StarRating from '../components/StarRating';
import ReviewCard from '../components/ReviewCard';
import ReportButton from '../components/ReportButton';
import BackLink from '../components/BackLink';
import Loader from '../components/Loader';
import { useI18n } from '../hooks/useI18n';

export default function StudentProfile() {
  const { t } = useI18n();
  const { id } = useParams();
  const { user } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    studentApi
      .get(id)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Loader label={t('profile.loading')} />;

  if (!data) {
    return (
      <div className="container section">
        <div className="alert alert-error">{error || t('profile.studentNotFound')}</div>
      </div>
    );
  }

  const { student, reviews, completedChores } = data;
  const isMe = user && String(user._id) === String(student._id);

  return (
    <>
      {/* ---------------- BANNER ---------------- */}
      <div className="profile-banner">
        <div className="container">
          <BackLink
            to="/dashboard"
            label={t('common.back')}
            style={{ color: 'var(--color-white)', opacity: 0.95 }}
          />
          <div className="profile-card">
            <Avatar person={student} size="lg" />

            <div className="profile-card-body">
              <div className="row" style={{ gap: 'var(--space-xs)' }}>
                <h1>
                  {student.firstName} {student.lastName}
                </h1>
                <Badge tone="amber">{student.schoolYear}</Badge>
              </div>

              {/* Spacing lives in the stylesheet, not inline: an inline style
                  beats every media query, and the phone layout needs to move
                  both of these rows. */}
              <div className="row profile-rating">
                <StarRating
                  value={student.rating?.average || 0}
                  count={student.rating?.count || 0}
                  size={18}
                />
              </div>

              <div className="row profile-meta">
                {student.location?.city && (
                  <span className="row" style={{ gap: 5 }}>
                    <MapPin size={15} aria-hidden="true" />
                    {student.location.city}
                  </span>
                )}
                {student.distanceKm !== null && student.distanceKm !== undefined && (
                  <span>{travel(student)}</span>
                )}
                {student.school && (
                  <span className="row" style={{ gap: 5 }}>
                    <GraduationCap size={15} aria-hidden="true" />
                    {student.school}
                  </span>
                )}
                <span>{t('profile.joined', { date: dateOnly(student.createdAt) })}</span>
              </div>
            </div>

            {isMe ? (
              <Link to="/settings" className="btn btn-inline" style={{ background: 'white', color: 'var(--color-blue)' }}>
                {t('profile.editMyProfile')}
              </Link>
            ) : (
              <ReportButton aboutUser={student._id} personName={student.firstName} />
            )}
          </div>
        </div>
      </div>

      {/* ---------------- BODY ---------------- */}
      <div className="container section">
        <div className="sidebar-layout">
          {/* ---- Sidebar ---- */}
          <div className="stack" style={{ gap: 'var(--space-md)' }}>
            <div className="card">
              <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('profile.trackRecord')}</h3>

              <div className="stack" style={{ gap: 'var(--space-xs)' }}>
                <div className="row-between">
                  <span className="row text-sm" style={{ gap: 8 }}>
                    <CheckCircle2 size={16} className="text-green" aria-hidden="true" />
                    {t('profile.choresCompleted')}
                  </span>
                  <strong>{student.stats?.choresCompleted || 0}</strong>
                </div>

                <div className="row-between">
                  <span className="text-sm">{t('profile.reviewsReceived')}</span>
                  <strong>{student.rating?.count || 0}</strong>
                </div>
              </div>
            </div>

            {Array.isArray(student.skills) && student.skills.length > 0 && (
              <div className="card">
                <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('profile.happyToDo')}</h3>
                <div className="row" style={{ gap: 6 }}>
                  {student.skills.map((skill) => (
                    <Badge key={skill} tone="soft-green">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ---- Main ---- */}
          <div className="stack" style={{ gap: 'var(--space-md)' }}>
            {student.bio && (
              <div className="card">
                <h3 style={{ marginBottom: 'var(--space-xs)' }}>About {student.firstName}</h3>
                <p style={{ whiteSpace: 'pre-line' }}>{student.bio}</p>
              </div>
            )}

            <div className="card">
              <h3 style={{ marginBottom: 'var(--space-sm)' }}>
                {t('profile.reviewsWithCount', { count: student.rating?.count || 0 })}
              </h3>

              {reviews.length === 0 ? (
                <p className="text-muted">
                  {t('profile.noReviewsBody')}
                </p>
              ) : (
                reviews.map((review) => <ReviewCard key={review._id} review={review} />)
              )}
            </div>

            {completedChores.length > 0 && (
              <div className="card">
                <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('profile.recentWork')}</h3>
                <div className="stack" style={{ gap: 0 }}>
                  {completedChores.map((chore) => (
                    <div className="detail-row" key={chore._id}>
                      <span style={{ flex: 1 }}>
                        <Link to={`/chores/${chore._id}`}>{chore.title}</Link>
                        <span className="text-sm text-muted"> · {chore.category}</span>
                      </span>
                      <span className="text-sm text-muted">{dateOnly(chore.completedAt)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
